import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    RadioGroup, Radio
} from '@mui/material';
import SuiButton from "../../components/SoftButton";
import { IoClose } from "react-icons/io5";
import {
    getSubjects,
    addSubject,
    checkDuplicatedArticles,
    getServices,
    getOutputFormat,
    extractPageCount,
    getOcrModes
} from "../../axios-client";
import data from '../../constants/languages.json';
import "../../mainComponents/departments/assets/departmentsStyleSheet.css";
import CircularProgress from '@mui/material/CircularProgress';
import MetaData from "./components/MetaData";
import FileUpload from "./components/FileUpload";
import SubjectTerms from "./components/SubjectTerms";
import PropTypes from "prop-types";
import Services from "./components/Services";
import { useTranslation } from "react-i18next";
import {useSoftUIController} from "../../context";
import OutputFormats from "./components/OutputFormats";
import { isSupportedImage } from 'constants/constants';

const DigitizeArticleDialog = ({ open, onClose, handleDigitizeArticle, isDigitizing, userCreditBalance }) => {
    const [controller] = useSoftUIController();

    const {direction} = controller;
  
    const [isFocused, setIsFocused] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        author: '',
        publicationDate: '',
        sourceLanguage: 'Arabic',
        targetLanguage: 'English',
        subjectTerms: [],
        hasMulticolumn: false,
        services: [],
        file: null,
        files: null,
        audioFormat: '',
        voiceName: ''
    });
    const [ocrModes, setOcrModes] = useState({ services: [] });
    const [ocrError, setOcrError] = useState(false);
    const [isOcrLoading, setIsOcrLoading] = useState(false);
    const [outputFormats, setOutputFormats] = useState({ services: [] });
    const [selectedOutputFormats, setSelectedOutputFormats] = useState([]);
    const [selectedMode, setSelectedMode] = useState("");
    const [errors, setErrors] = useState({});
    const [searchSubject, setSearchSubject] = useState('');
    const [isAddingSubject, setIsAddingSubject] = useState(false);
    const [existingArticleId, setExistingArticleId] = useState(null);
    const [disableNextButton, setDisableNextButton] = useState(true);
    const [uploadingInProgress, setUploadingInProgress] = useState(false);
    const [services, setServices] = useState([]);
    const [step, setStep] = useState(1);
    const [totalCost, setTotalCost] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [fixedAmount, setFixedAmount] = useState(0.75)
    const { t } = useTranslation("publicationsTranslation");
    const { i18n } = useTranslation();
    const dir = i18n.dir();
    const currentLanguage = i18n.language;

    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: `${isFocused ? '#cdcecf' : '#dcdfe3'}`,
        borderRadius: '8px',
        padding: '5px 10px',
        marginBottom: '10px',
        transition: 'border-color 0.3s ease',
    };

    const inputStyle = {
        width: '100%',
        padding: '5px 0px',
        border: 'none',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'Arial, Roboto',
    };




    useEffect(() => {
        setLanguages(
            data.map(lang => ({
                ...lang,
                label: i18n.language === 'ar'
                ? lang.language_ar || lang.language
                : lang.language
            }))
        );
        getSubjects(currentLanguage)
            .then(res => {
                setSubjects(res.data);
            })
            .catch(error => {
                console.error('Error fetching subjects:', error);
            });

        getServices(currentLanguage)
            .then(res => {
                const { services: fetchedServices, fixed_amount } = res.data; 
                setServices(fetchedServices);
                setFixedAmount(fixed_amount);  
                const defaultSelectedServices = fetchedServices
                    .filter(service => (service.pre_selected == true))
                    .map(key => key.id);
                setFormData(prevState => ({
                    ...prevState,
                    services: defaultSelectedServices
                }));

                const initialCost = fetchedServices
                    .filter(service => defaultSelectedServices.includes(service.id))
                    .reduce((acc, service) => acc + (service.price || 0), 0);
                setTotalCost(fixed_amount + (pageCount * initialCost));
            })
            .catch(error => {
                console.error('Error fetching services:');
            });
    }, []);

    useEffect(() => {
        let serviceCost = formData.services.reduce((acc, id) => {
            const service = services.find((s) => s.id === id);
            return service ? acc + (service.price || 0) : acc;
        }, 0);

        const ocrModeCost = (() => {
            const mode = ocrModes?.services?.find((s) => s.id === selectedMode);
            return mode ? (mode.price || 0) : 0;
        })();

        // Output format cost from outputFormats.services

        const outputFormatsCost = selectedOutputFormats?.reduce((acc, id) => {
            const format = outputFormats?.services?.find((s) => s.id === id);
            return format ? acc + (format.price || 0) : acc;
        }, 0);




        // Calculate the total cost and round to 2 decimal places
        const total = fixedAmount + pageCount * (serviceCost + ocrModeCost + outputFormatsCost);
        setTotalCost(parseFloat(total.toFixed(2)));
    }, [formData.services, selectedMode, selectedOutputFormats, services, pageCount]);
    

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };


    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleBlur = async () => {
        if (formData.title && formData.author) {
            try {
                const response = await checkDuplicatedArticles({ params: { title: formData.title, author: formData.author } });
                if (response.data.exists) {
                    setErrors(prevErrors => ({
                        ...prevErrors,
                        title: 'An article with this title and author already exists!'
                    }));
                    setExistingArticleId(response.data.data.id);
                } else {
                    setExistingArticleId(null);
                }
            } catch (error) {
                console.error("Error checking duplicated articles:", error);
            }
        }
    };

    const handleService = (e, serviceId) => {
        if (formData.services.includes(serviceId)) {
            setFormData(prevState => ({
                ...prevState,
                services: formData.services.filter(key => key != serviceId)
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                services: [...prevState.services, serviceId]
            }));
        }
    };


    const handleFileUpload = (uploadedFiles) => {

        if (!uploadedFiles.length) return;

        setDisableNextButton(true);

        const docFile = uploadedFiles.find(f =>
                f.type === 'application/pdf' ||
                f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                f.type === 'application/msword'
        );

        const imageFiles = uploadedFiles.filter(isSupportedImage);

        if (docFile) {
            setErrors(prev => ({ ...prev, file: '' }));
            setFormData(prev => ({
                ...prev,
                file: docFile,
                title: prev.title || docFile.name.replace(/\.[^/.]+$/, "")
            }));

            setUploadingInProgress(true);
            extractPageCount(docFile)
                .then((res) => {
                    if (res.data?.pageCount) setPageCount(res.data.pageCount);
                    setUploadingInProgress(false);
                    setDisableNextButton(false);
                })
                .catch((error) => {
                    console.error("Error extracting page count:", error);
                    setPageCount(0);
                    setUploadingInProgress(false);
                    setDisableNextButton(true);
                });
        }

        if (imageFiles.length) {
            setErrors(prev => ({ ...prev, files: '' }));
            setFormData(prev => ({
                ...prev,
                files: imageFiles
            }));
            setPageCount(imageFiles.length);
            setDisableNextButton(false);
        }

        if (!docFile && imageFiles.length === 0) {
            setErrors(prev => ({
                ...prev,
                file: 'Only PDF or image files are allowed',
                files: 'Only image files are allowed'
            }));
            setDisableNextButton(true);
        }
    };


    // In Step 1, only Title and File are required.
    const validateForm = () => {
        let newErrors = {};
        if (!formData.title) newErrors.title = 'Title is required';
        if (!formData.file && !formData.files) newErrors.file = 'File upload is required';
        const audioBookService = services.find(service => service.name === 'Text to Speech');
        if(formData.services?.includes(audioBookService?.id)){
            if (!formData.audioFormat) newErrors.audioFormat = 'Audio format is required';
            if (!formData.voiceName) newErrors.voiceName = 'Voice Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDigitization = () => {
        if (validateForm() && !isDigitizing && !existingArticleId) {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('author', formData.author);
            data.append('publication_date', formData.publicationDate);
            data.append('source_language', formData.sourceLanguage);
            data.append('target_language', formData.targetLanguage);
            if(formData.file){
                data.append('file', formData.file);
            } else {
                for (let i = 0; i < formData.files.length; i++) {
                    data.append('files', formData.files[i]);
                }
            }
            data.append('audio_format', formData.audioFormat);
            data.append('voice_name', formData.voiceName);
            formData.services.forEach(service => {
                data.append('services', service);
            });
            formData.subjectTerms.forEach(subjectId => {
                data.append('subjects', subjectId);
            });
            handleDigitizeArticle(data);
        }
    };

    const getCompatibleOutputFormats = (modeId, modes = ocrModes.services) =>
        modes.find(mode => mode.id === modeId)?.compatible_output_formats ?? [];

    // Keep the OCR mode and output formats of formData.services in sync with the selection
    const applyOcrSelection = (modeId, formatIds, modes = ocrModes.services, formats = outputFormats.services) => {
        const compatibleFormatIds = formatIds.filter(id => getCompatibleOutputFormats(modeId, modes).includes(id));
        const managedIds = new Set([...modes, ...formats].map(service => service.id));

        setSelectedMode(modeId);
        setSelectedOutputFormats(compatibleFormatIds);
        setFormData(prev => ({
            ...prev,
            services: [
                ...prev.services.filter(id => !managedIds.has(id)),
                ...(modeId ? [modeId] : []),
                ...compatibleFormatIds
            ]
        }));
    };

    const handleOcrModeChange = (modeId) => {
        // Formats not available with the new mode are deselected
        applyOcrSelection(modeId, selectedOutputFormats);
    };

    const handleOutputFormatToggle = (formatId) => {
        const updatedFormats = selectedOutputFormats.includes(formatId)
            ? selectedOutputFormats.filter(id => id !== formatId)
            : [...selectedOutputFormats, formatId];
        applyOcrSelection(selectedMode, updatedFormats);
    };

    const handleAddNewSubject = () => {
        if (!searchSubject.trim()) return;
        setIsAddingSubject(true);
        addSubject({ name: searchSubject, lang: currentLanguage })
            .then(res => {
                return getSubjects(currentLanguage);
            })
            .then(res => {
                setSubjects(res.data);
                const newSubjectId = res.data.find(subject => subject.name === searchSubject)?.id;
                if (newSubjectId) {
                    setFormData(prevState => ({
                        ...prevState,
                        subjectTerms: [...prevState.subjectTerms, newSubjectId]
                    }));
                }
                setSearchSubject('');
            })
            .catch(error => {
                console.error('Error adding subject:', error);
            })
            .finally(() => {
                setIsAddingSubject(false);
            });
    };

    const handleNext = async () => {
        if (!formData.title) {
            setErrors(prevErrors => ({...prevErrors, title: 'Title is required'}));
            return;
        }
        if (formData.file || formData.files) {
            setStep(2);
        } else {
            setErrors(prevErrors => ({...prevErrors, file: 'Please upload a file to proceed'}));
            return;
        }
        setIsOcrLoading(true);
        setOcrError(false);

        let modes = [];
        let formats = [];
        try {
            const res = await getOcrModes(currentLanguage);
            modes = res.data?.services || [];
            if (modes.length === 0) {
                setOcrError(true);
            }

        } catch (error) {
            console.error('Error fetching OCR modes:', error);
            setOcrError(true);
        }

        try {
            const res = await getOutputFormat(currentLanguage);
             formats = res.data?.services || [];
        } catch (error) {
            console.error('Error fetching output formats:', error);
        }

        setOcrModes({ services: modes });
        setOutputFormats({ services: formats });

        // Keep the user's previous choices when coming back to this step, otherwise use the defaults
        const modeId = modes.some(mode => mode.id === selectedMode)
            ? selectedMode
            : (modes.find(mode => mode.pre_selected)?.id ?? "");
        const formatIds = selectedOutputFormats.length
            ? selectedOutputFormats
            : formats.filter(format => format.pre_selected).map(format => format.id);
        applyOcrSelection(modeId, formatIds, modes, formats);

        setIsOcrLoading(false);

    };



    const handleBack = () => {
        setStep(1);
    };

    return (
        <Dialog open={open} aria-labelledby="form-dialog-title" maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 10 } }}>
            <DialogTitle
                id="form-dialog-title"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f5f5f5', padding: '16px' }}
            >
                {step === 1 ? t("digitizeDialogMetadata") : t("digitizeDialogServices")}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    style={{ position: 'absolute', [dir === 'rtl' ? 'left' : 'right']: 8, top: 8, cursor: 'pointer' }}
                />
            </DialogTitle>
            <DialogContent sx={{ padding: "0px 24px", margin: "5px 0" }}>
                {step === 1 && (
                    <>
                        <FileUpload
                            setFormData={setFormData}
                            setErrors={setErrors}
                            handleFileUpload={handleFileUpload}
                            formData={formData}
                            errors={errors}
                            isUploading={uploadingInProgress}
                            setUploadingInProgress={setUploadingInProgress}
                            setDisableNextButton={setDisableNextButton}
                        />
                        <MetaData
                            inputContainerStyle={inputContainerStyle}
                            inputStyle={inputStyle}
                            formData={formData}
                            handleInputChange={handleInputChange}
                            setIsFocused={setIsFocused}
                            errors={errors}
                            handleSelectChange={handleSelectChange}
                            languages={languages}
                            handleBlur={handleBlur}
                            isEditable={true}
                            requiredTitle={true}
                            requiredSourceLanguage={false}
                            requiredTargetLanguage={false}
                        />
                        <SubjectTerms subjects={subjects} setSearchSubject={setSearchSubject} handleAddNewSubject={handleAddNewSubject}
                                      isAddingSubject={isAddingSubject} searchSubject={searchSubject} formData={formData} setFormData={setFormData}
                        />
                    </>
                )}
                {step === 2 && (
                    <>
                        <Box sx={{ backgroundColor: '#fffff', borderRadius: '8px' }}>

                            <>
                                <label htmlFor="layout" className="label-style">{t("ocrModes")}</label>
                                {isOcrLoading ? (
                                    <CircularProgress size={24} />
                                ) : ocrError || (ocrModes.services.length === 0) ? (
                                    <Typography color="error" sx={{ fontSize: '0.85rem', mt: 1 }}>
                                        {t('ocrError')}
                                    </Typography>
                                ) : (
                                    <RadioGroup
                                        name="ocrModes"
                                        value={selectedMode}
                                    >
                                        {ocrModes.services.map((service) => (
                                            <Box
                                                key={service.id}
                                                display="flex"
                                                flexDirection="row"
                                                alignItems="flex-start"
                                                mb={2}
                                            >
                                                <Radio
                                                    checked={selectedMode === service.id}
                                                    onChange={() => handleOcrModeChange(service.id)}
                                                    value={service.id}
                                                    sx={{ mt: 0.5, ...(dir === "rtl" ? { ml: 2 } : { mr: 2 }) }}
                                                />
                                                <Box>
                                                    <Typography  sx={{ fontWeight: 600, fontSize: '0.9rem', color: "#3f4a63" }}>
                                                        {service.slug}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '9.5px', color: "#828a9b" }}>
                                                        {service.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </RadioGroup>
                                    )}
                            </>
                        </Box>
                        <Box sx={{ backgroundColor: '#fffff', borderRadius: '8px' }}>
                            <Services
                                label={t("digitizeDialogServicesLabel")}
                                formData={formData}
                                setFormData={setFormData}
                                selectedLanguage={formData['sourceLanguage'].toLowerCase()}
                                selectedTargetLanguage={formData['targetLanguage'].toLowerCase()}
                                services={services} selectedServices={formData.services}
                                handleCheckboxServiceChange={handleService}
                                errors={errors}
                                setErrors={setErrors}
                            />
                        </Box>
                        {!isOcrLoading && (
                            <Box sx={{ backgroundColor: '#fffff', borderRadius: '8px' }}>
                                <OutputFormats
                                    label={t("outputFormat")}
                                    hint={t("InAdditionToWord")}
                                    outputFormats={outputFormats.services}
                                    ocrModes={ocrModes.services}
                                    selectedOcrMode={selectedMode}
                                    selectedOutputFormats={selectedOutputFormats}
                                    onToggle={handleOutputFormatToggle}
                                />
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            {step === 2 && <DialogContent sx={{padding: "0px 24px", overflowY:"clip" }}>
                <Box
                    sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: '#fffff',
                        borderRadius: 2,
                        border: '1px solid #cce4f7',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        mt: 2
                    }}
                >
                    <Typography variant="h6" sx={{fontWeight: 'bold', color: '#0066cc'}}>
                        {t("digitizeDialogTotalCost")}: ${totalCost}
                    </Typography>
                    <Typography variant="caption" sx={{color: '#555', display: 'block', mt: 1}}>
                        {t("digitizeDialogPageCount", { count: pageCount })}
                    </Typography>
                    {userCreditBalance > 0 && userCreditBalance < totalCost && (
                        <Typography variant="caption" sx={{color: '#cc0000', display: 'block', mt: 2}}>
                            {t("digitizeDialogExceedCredits")}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            }
            <DialogActions>
                <SuiButton variant="gradient" color="secondary" size="medium" onClick={onClose}>{t('close')}</SuiButton>
                {step === 1 ? (
                    <SuiButton variant="gradient" color="primary" size="medium" onClick={handleNext} disabled={disableNextButton}
                               sx={{ml: 1, mr:  (direction === 'rtl' ? 1 : 0),
                    }}>
                        {t("next")}
                    </SuiButton>
                ) : (
                    <>
                        <SuiButton variant="gradient" color="secondary" size="medium" onClick={handleBack} disabled={isDigitizing}
                                   sx={{ml: 1, mr:  (direction === 'rtl' ? 1 : 0),
                        }}>
                            {t("back")}
                        </SuiButton>
                        <SuiButton variant="gradient" color="primary" size="medium" onClick={handleDigitization}
                                   disabled={existingArticleId || ocrError || isOcrLoading || !selectedMode}>
                            {isDigitizing ? t("digitizing") : t("digitize")}
                            {isDigitizing && <CircularProgress size={20} style={{ marginLeft: 10, color: "white" }} />}
                        </SuiButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
    
};

DigitizeArticleDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    handleDigitizeArticle: PropTypes.func,
    isDigitizing: PropTypes.bool,
    userCreditBalance: PropTypes.number
};
export default DigitizeArticleDialog;
